<template>
  <div class="profile">
    <div class="profile__head">
      <div class="profile__elements"></div>
    </div>
    <div class="profile__body">
      <div class="profile__main">
        <user-profile
          class="profile__main-image"
          :name="clientName"
          :picture="profileUrl"
        />
        <div class="profile__main-title">
          <span v-if="!editName" @click="changEditName" role="button">
            {{ clientName }}
          </span>
          <span v-else class="profile__main-title-input">
            <hcc-input
              class="modal-input"
              v-model.trim="clientNameInput"
              @keyup.enter="submitName"
            />
            <cancel-icon
              role="button"
              class="profile__main-title-icon red"
              @click="cancelEditName"
            />
            <check-single-icon
              role="button"
              class="profile__main-title-icon green"
              @click="submitName"
            />
          </span>
        </div>
        <div v-if="!hideUsername" class="profile__main-subtitle">
          {{ clientUsername }}
        </div>
        <div class="profile__main-campaign">
          {{ campaignName }}
        </div>

        <div class="profile__main-subtitle">
          <channel-badge :type="channelType" class="profile__main-channel" />
          <div>{{ channelName }}</div>
        </div>
        <div
          v-if="isWhatsAppChannel"
          class="profile__main-time"
          :class="isWarning && 'warning'"
        >
          <div v-if="hasExpired">{{ $t("chat.has-expired") }}</div>
          <div v-else>
            {{ $t("chat.expired-whatsapp") }}
            <span>{{ expiredTime() }}</span>
          </div>
        </div>
      </div>
      <div class="profile__scrollable">
        <vue-scroll>
          <div class="profile__block profile__block--add-border">
            <div
              class="profile__action profile__action—pull-left"
              @click="openMultimedia"
              role="button"
            >
              <div class="profile__action-icon">
                <database-icon />
              </div>
              <div class="profile__action-text">
                {{ $t("multimedia.name") }}
              </div>
            </div>
          </div>
          <labels
            :campaignId="campaignId"
            :clientId="clientId"
            :userLabels="userLabels || []"
          />

          <div class="profile__block">
            <div
              @click="toggleHistory"
              :class="{ 'is-opened': showHistory }"
              class="profile__action profile__action--toggle"
              role="button"
            >
              <div class="profile__action-icon">
                <ellipsis-icon />
              </div>
              <div class="profile__action-text">
                {{ $t("chat.last-conversation") }}
              </div>
            </div>
            <template v-if="showHistory">
              <div class="profile__history">
                <div v-if="!hasHistory" class="profile__no-content">
                  {{ $t("chat.no-history") }}
                </div>
                <profile-history-item
                  v-else
                  v-for="(h, index) in history"
                  :key="`${clientId}-${index}`"
                  :history="h"
                />
              </div>
            </template>
          </div>
        </vue-scroll>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import {
  CONVERSATION_LOAD_HISTORY,
} from '@/eventTypes';
import EventBus from '@/eventBus';
import saveLabelGql from '@/graphql/mutations/saveLabel.gql';
import DatabaseIcon from '@/assets/icons/database.svg';
import UserProfile from '@/components/UserProfile.vue';
import EllipsisIcon from '@/assets/icons/ellipsis.svg';
import ChannelBadge from '@/components/ChannelBadge.vue';

export default {
  components: {
    DatabaseIcon,
    UserProfile,
    EllipsisIcon,
    ChannelBadge,
    Labels: () => import('@/components/labels/index.vue'),
    HccInput: () => import('@/components/shared/HccInput/index.vue'),
    CheckSingleIcon: () => import('@/assets/icons/check-single.svg'),
    CancelIcon: () => import('@/assets/icons/cancel.svg'),
    ProfileHistoryItem: () => import('@/components/ProfileHistoryItem.vue'),
  },
  data() {
    return {
      idClient: '',
      requestUsers: false,
      clientNameInput: null,
      editName: false,
      closeConversationAction: '',
      showHistory: false,
    };
  },
  computed: {
    ...mapGetters([
      'activeConversationHistory',
      'getConfig',
    ]),
    client() {
      return this.activeConversationHistory ? this.activeConversationHistory.client ?? {} : {};
    },
    conversation() {
      return this.activeConversationHistory
        ? this.activeConversationHistory.conversation ?? {} : {};
    },
    campaign() {
      return this.conversation.campaign ?? {};
    },
    channel() {
      return this.conversation.channel ?? {};
    },
    campaignId() {
      return this.campaign.id ?? null;
    },
    channelId() {
      return this.channel.id ?? null;
    },
    clientId() {
      return this.client.id ?? null;
    },
    clientName() {
      return this.client.fullName || this.client.name || this.client.username || '';
    },
    clientUsername() {
      return this.client.username;
    },
    channelName() {
      return this.channel ? this.channel.name
        : '';
    },
    campaignName() {
      return this.campaign ? this.campaign.name
        : '';
    },
    channelType() {
      return this.channel && this.channel.channelType.key ? this.channel.channelType.key : '';
    },
    userLabels() {
      return this.client.labels;
    },
    profileUrl() {
      return this.client.profilePic;
    },
    history() {
      return this.client.history ?? [];
    },
    historyLoaded() {
      return this.client.history !== undefined;
    },
    hasHistory() {
      return this.history.length > 0;
    },
    configuration() {
      return this.getConfig(this.campaignId, this.channelId);
    },
    lastMessageAt() {
      return this.conversation.lastMessageAt;
    },
    session24h() {
      return this.$moment(this.lastMessageAt).add(24, 'hours');
    },
    hideUsername() {
      return this.configuration.hideUsername ?? false;
    },
    duration() {
      return this.$moment.duration(this.session24h.diff(this.currentTime));
    },
    hasExpired() {
      return (this.duration.hours() <= 0 && this.duration.hours() !== 23
      && this.duration.minutes() <= 0 && this.duration.minutes() !== 59)
      || !this.duration.isValid();
    },
    isWarning() {
      return this.duration.hours() <= 1 ? 'warning' : 'normal';
    },
    isWhatsAppChannel() {
      return this.channel.channelType?.key === 'whatsapp';
    },
  },
  watch: {
    client() {
      if (!this.historyLoaded) {
        EventBus.$emit(CONVERSATION_LOAD_HISTORY, this.clientId);
      }
    },
  },
  methods: {
    ...mapMutations(['setNameClient']),
    resetName() {
      this.clientNameInput = null;
    },
    changEditName() {
      this.clientNameInput = this.clientName;
      this.editName = true;
    },
    cancelEditName() {
      this.editName = false;
      this.clientNameInput = null;
    },
    async submitName() {
      if (this.clientNameInput !== null && this.clientNameInput !== ''
        && this.clientNameInput !== this.clientName) {
        const { data: result } = await this.$apollo.mutate({
          mutation: saveLabelGql,
          variables: {
            id: this.clientId,
            name: this.clientNameInput,
          },
        });
        if (result.updateClient) {
          this.cancelEditName();
          this.editName = false;
          this.setNameClient({
            clientId: this.clientId,
            name: result.updateClient.name,
            fullName: result.updateClient.fullName,
          });
        }
      } else {
        this.cancelEditName();
      }
    },
    openMultimedia() {
      this.idClient = this.clientId;
      const params = {
        clientId: this.idClient,
        channelId: this.channelId,
      };

      this.$modal.show('user-multimedia', params);
    },
    toggleHistory() {
      this.showHistory = !this.showHistory;
    },
    expiredTime() {
      if (this.duration.hours === 0 && this.duration.minutes > 0) {
        return `${this.duration.minutes()}m`;
      }
      return `${this.duration.hours()}h ${this.duration.minutes()}m`;
    },
  },
};
</script>
